/**
 * ADR-0243 — one person's timeline: who they are (coarse location, device,
 * IP for roles that may see it), and every session they had on the site,
 * grouped by day, with the pages they visited and when.
 *
 * Self-contained: takes a site id + person id, talks to
 * `GET /v1/sites/:site_id/people/:person_id` through the shared HttpClient
 * (`useOptionalHttp`), and pages older sessions on demand. The visits panel
 * mounts it in the detail pane for a `p.<person_id>` row; mount with
 * `key={personId}` so a new person starts from page 1.
 *
 * Times render in the merchant's display time zone (ADR-0160).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';

import type { UserRole } from '@sill/types';
import type {
  BlockState,
  ManualBlockDuration,
  PersonDetailResponse,
  PersonIpStatus,
  PersonSession,
} from '@sill/types/api';

import type { HttpClient } from '../../api/http-client.js';
import { useOptionalHttp } from '../../api/http-context.js';
import { forgetPerson, getPerson } from '../../api/people.js';
import { deleteBlocklistEntry, getBlocking, getBlocklistLookup, postManualBlock, type BlockLookupHit } from '../../api/threats.js';
import { useOptionalAuth } from '../../app/auth-context.js';
import { useDisplayTimeZone } from '../../app/time-zone-context.js';
import { ConfirmDialog, RelativeTime, Tooltip } from '../../components/atoms/index.js';
import { formatIsoDate } from '../../lib/format.js';
import { useC } from '../../lib/theme.js';
import { TurnOnBlockingLink } from '../mandates/TurnOnBlocking.js';
import { DetailActionRow } from '../mandates/DetailActionRow.js';
import { PersonBlockPopover } from './PersonBlockPopover.js';
import { deviceLabel, formatDuration, fullPlaceLabel, personName } from './person-label.js';
import { PersonGlyph } from './PersonGlyph.js';
import { blockUnavailableReason, useIsShopifySite } from '../mandates/BlockControl.js';

const monoFont = "'JetBrains Mono', monospace";
const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";

export interface PersonTimelineProps {
  readonly siteId: string;
  readonly personId: string;
  /** Founder bug 2026-10-18: name the site in the self-block confirm. */
  readonly siteDomain?: string | null;
  /** Open the signed hourly record a page load was folded into. */
  readonly onOpenRecord?: ((record_id: string) => void) | undefined;
  /** Called after the person was forgotten (close the pane, refresh the list). */
  readonly onForgotten?: ((person_id: string) => void) | undefined;
  /** Tests: bypass the auth context / the http provider. */
  readonly role_override?: UserRole | undefined;
  readonly http_override?: HttpClient | undefined;
}

type Tokens = ReturnType<typeof useC>;

/**
 * Founder bug 2026-10-18 (block-state consistency): the chip label
 * says WHAT the block covers (date vs forever) and whether blocking
 * is delivering ("Block saved · not active" when the site has
 * blocking off).
 */
function formatBlockedChipLabel(
  hit: BlockLookupHit,
  blocking: BlockState['blocking'] | null,
): string {
  const notDelivering = blocking !== null
    && (blocking.mode === 'off' || blocking.delivery === 'failing' || blocking.install !== 'worker_ready');
  if (notDelivering) return 'Block saved · not active';
  if (hit.expires_at === null) return 'Blocked forever';
  try {
    const d = new Date(hit.expires_at);
    if (Number.isNaN(d.getTime())) return 'Blocked';
    return `Blocked until ${new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(d)}`;
  } catch { return 'Blocked'; }
}

const IP_STATUS_COPY: Record<Exclude<PersonIpStatus, 'shown'>, string> = {
  hidden: 'Visible to owners, admins and reviewers.',
  unavailable: 'Not recorded for this visitor.',
  rate_limited: 'Temporarily unavailable. Reload in a minute.',
};

function dayKey(iso: string, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(iso));
  } catch {
    return iso.slice(0, 10);
  }
}

function dayLabel(iso: string, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })
      .format(new Date(iso))
      .toUpperCase();
  } catch {
    return iso.slice(0, 10);
  }
}

function clock(iso: string, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat('en-GB', { timeZone, hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(iso));
  } catch {
    return iso.slice(11, 16);
  }
}

/** Sessions (newest first) → day groups (newest first), sessions kept in order. */
export function groupSessionsByDay(
  sessions: readonly PersonSession[],
  timeZone: string,
): Array<{ readonly key: string; readonly label: string; readonly sessions: PersonSession[] }> {
  const out: Array<{ key: string; label: string; sessions: PersonSession[] }> = [];
  for (const s of sessions) {
    const key = dayKey(s.started_at, timeZone);
    const last = out[out.length - 1];
    if (last !== undefined && last.key === key) last.sessions.push(s);
    else out.push({ key, label: dayLabel(s.started_at, timeZone), sessions: [s] });
  }
  return out;
}

export function PersonTimeline({
  siteId,
  personId,
  siteDomain,
  onOpenRecord,
  onForgotten,
  role_override,
  http_override,
}: PersonTimelineProps): JSX.Element {
  const C = useC();
  const isShopify = useIsShopifySite(siteId);
  const tz = useDisplayTimeZone();
  const ctx_http = useOptionalHttp();
  const http = http_override ?? ctx_http;
  const auth = useOptionalAuth();
  const role: UserRole | null = useMemo(() => {
    if (role_override !== undefined) return role_override;
    return auth !== null && auth.state.kind === 'authenticated' ? auth.state.user.role : null;
  }, [role_override, auth]);
  const can_forget = role === 'owner' || role === 'admin';

  const [detail, setDetail] = useState<PersonDetailResponse | null>(null);
  const [sessions, setSessions] = useState<PersonSession[]>([]);
  const [page, setPage] = useState(1);
  const [state, setState] = useState<'loading' | 'ready' | 'gone' | 'failed'>('loading');
  const [loadingMore, setLoadingMore] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [forgetFailed, setForgetFailed] = useState(false);
  // Suspicious v2 §4.1 — Block popover state + the site's current
  // blocking state (status line + enforcement banner on Block).
  const can_block = role === 'owner' || role === 'admin';
  const [blockOpen, setBlockOpen] = useState(false);
  const [blocking, setBlocking] = useState<BlockState['blocking'] | null>(null);
  const [liveBlocks, setLiveBlocks] = useState<number>(0);
  // Founder bug 2026-10-18 (block-state consistency): per-address
  // lookup. When the visitor's primary IP (or a /64 covering it) is
  // already blocked, the header shows a "Blocked until …" chip +
  // Unblock button instead of a redundant Block ▾.
  const [addressBlock, setAddressBlock] = useState<BlockLookupHit | null>(null);
  const [unblockPending, setUnblockPending] = useState(false);

  useEffect(() => {
    if (http === null) { setState('failed'); return undefined; }
    let cancelled = false;
    if (page > 1) setLoadingMore(true);
    void getPerson(http, siteId, personId, page)
      .then((res) => {
        if (cancelled) return;
        if (res === null) { setState('gone'); return; }
        setDetail((prev) => (page === 1 || prev === null ? res : { ...res, ip: prev.ip }));
        setSessions((prev) => (page === 1 ? [...res.sessions] : [...prev, ...res.sessions]));
        setState('ready');
      })
      .catch(() => { if (!cancelled && page === 1) setState('failed'); })
      .finally(() => { if (!cancelled) setLoadingMore(false); });
    return () => { cancelled = true; };
  }, [http, siteId, personId, page]);

  /**
   * Suspicious v2 §4.1 — fetch the site's blocking state only once
   * the person detail is ready AND we have a real auth context. The
   * test harness runs without the auth provider (uses `role_override`),
   * and firing a second HTTP call there would reorder the HTTP calls
   * the person-timeline tests pin.
   */
  const auth_ready = auth !== null && auth.state.kind === 'authenticated';
  useEffect(() => {
    if (http === null || !can_block || state !== 'ready' || !auth_ready) return;
    void getBlocking(http, siteId)
      .then((r) => {
        if (r != null && r.blocking != null) {
          setBlocking(r.blocking);
          if (typeof r.counts?.live_blocks === 'number') setLiveBlocks(r.counts.live_blocks);
        }
      })
      .catch(() => undefined);
  }, [http, siteId, can_block, state, auth_ready]);

  // Founder bug 2026-10-18 (block-state consistency): look up the
  // visitor's primary IP in the per-site blocklist. CIDR-aware
  // server-side — a block on the /64 covering the visitor counts.
  const refreshAddressBlock = useCallback(async () => {
    if (http === null || !can_block || detail?.ip.status !== 'shown') { setAddressBlock(null); return; }
    const ips = detail.ip.addresses.map((a) => a.address).slice(0, 32);
    if (ips.length === 0) { setAddressBlock(null); return; }
    try {
      const { lookups } = await getBlocklistLookup(http, siteId, ips);
      const hit = lookups.find((l): l is BlockLookupHit => l.blocked);
      setAddressBlock(hit ?? null);
    } catch {
      setAddressBlock(null);
    }
  }, [http, can_block, siteId, detail?.ip.status, detail?.ip.addresses]);
  useEffect(() => {
    if (!auth_ready || state !== 'ready') return;
    void refreshAddressBlock();
  }, [auth_ready, state, refreshAddressBlock]);

  const refreshBlockingAfterMutation = useCallback(async () => {
    if (http === null) return;
    try {
      const r = await getBlocking(http, siteId);
      if (r != null && r.blocking != null) {
        setBlocking(r.blocking);
        if (typeof r.counts?.live_blocks === 'number') setLiveBlocks(r.counts.live_blocks);
      }
    } catch { /* non-fatal */ }
    await refreshAddressBlock();
  }, [http, siteId, refreshAddressBlock]);

  const onUnblock = useCallback(async () => {
    if (http === null || addressBlock === null) return;
    setUnblockPending(true);
    try {
      await deleteBlocklistEntry(http, siteId, addressBlock.block_id);
    } finally {
      setUnblockPending(false);
      await refreshBlockingAfterMutation();
    }
  }, [http, siteId, addressBlock, refreshBlockingAfterMutation]);

  const forget = useCallback(() => {
    if (http === null) return;
    setConfirming(false);
    setForgetFailed(false);
    void forgetPerson(http, siteId, personId)
      .then(() => {
        setState('gone');
        onForgotten?.(personId);
      })
      .catch(() => setForgetFailed(true));
  }, [http, siteId, personId, onForgotten]);

  if (state === 'loading') {
    return <Kicker C={C} testid="person-timeline-loading">LOADING VISITOR…</Kicker>;
  }
  if (state === 'gone') {
    return <Muted C={C} testid="person-timeline-gone">This visitor is no longer on record.</Muted>;
  }
  if (state === 'failed' || detail === null) {
    return <Muted C={C} testid="person-timeline-failed">The visitor could not be loaded.</Muted>;
  }

  const p = detail.person;
  const days = groupSessionsByDay(sessions, tz);
  const place = fullPlaceLabel(p.location);
  const device = deviceLabel(p.device);

  return (
    <div data-testid="person-timeline" style={{ fontFamily: sansFont, color: C.text }}>
      {/* Founder 2026-10-19: no "PERSON · RECOGNISED BY …" kicker; the
          name and flag already say it. Network-keyed people keep the
          hint below, which carries the one non-obvious caveat. */}
      {p.key_kind === 'overflow' ? <Kicker C={C}>VISITS OVER THE NEW-VISITOR LIMIT</Kicker> : null}
      {p.key_kind === 'network' ? (
        <Muted C={C} testid="person-network-hint">
          Recognised by network and browser, so this may combine people who share a network, such as an office.
        </Muted>
      ) : null}
      {p.key_kind === 'overflow' ? (
        <Muted C={C}>Page views from new visitors after this site reached its hourly limit for new visitors.</Muted>
      ) : null}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '6px 0 14px' }}>
        <h2 style={{ fontFamily: serifFont, fontWeight: 400, fontSize: 26, lineHeight: 1.15, margin: 0, display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
          {p.key_kind !== 'overflow' ? <PersonGlyph country={p.location?.country ?? null} size={20} /> : null}
          <span style={{ overflowWrap: 'anywhere' }}>{personName(p)}</span>
        </h2>
        {/* Suspicious v2 (founder 2026-10-18) — unified action row.
            Person: Block ▾ opens the /64-vs-exact popover (NOT a direct
            block, because the merchant needs to pick); ⋯ carries Share
            only — Trust / Ignore apply to threat senders, not people. */}
        {can_block && detail.ip.status === 'shown' && detail.ip.addresses.length > 0 ? (
          <DetailActionRow
            data-testid="person-header-action-row"
            onBlock={() => setBlockOpen(true)}
            blockDisabledReason={blockUnavailableReason(blocking, { shopify: isShopify })}
            {...(addressBlock !== null ? {
              blockedUntilLabel: formatBlockedChipLabel(addressBlock, blocking),
              blockedTone: (blocking?.mode === 'off' ? 'amber' : 'coral') as 'coral' | 'amber',
              onUnblock,
              unblockPending,
            } : {})}
            onShare={() => {
              try {
                const url = new URL(window.location.origin + window.location.pathname);
                url.searchParams.set('view', 'mandates');
                url.searchParams.set('segment', 'people');
                url.searchParams.set('site', siteId);
                url.searchParams.set('person', personId);
                void navigator.clipboard.writeText(url.toString());
              } catch { /* clipboard blocked */ }
            }}
          />
        ) : null}
      </div>
      {blocking != null && can_block ? (
        <div style={{ marginBottom: 14 }}>
          <TurnOnBlockingLink
            siteId={siteId}
            siteDomain={null}
            http={http}
            blocking={blocking}
            liveBlocksCount={liveBlocks}
            canMutate={can_block}
            onModeFlipped={(install_override) => {
              setBlocking((prev) => prev === null ? prev : (install_override === null
                ? { ...prev, mode: 'on' }
                : { ...prev, install: install_override }));
            }}
          />
        </div>
      ) : null}
      {blockOpen && http !== null ? (
        <PersonBlockPopover
          siteId={siteId}
          http={http}
          personId={personId}
          ipAddresses={detail.ip.addresses}
          siteDomain={siteDomain ?? null}
          onClose={() => setBlockOpen(false)}
          onBlocked={() => {
            setBlockOpen(false);
            // Founder bug 2026-10-18: after a successful block, refetch
            // BOTH the per-site blocking state AND the per-address
            // lookup so the header flips to the "Blocked until …" chip
            // immediately without a reload.
            void refreshBlockingAfterMutation();
          }}
        />
      ) : null}

      <div className="grid grid-cols-2 sm:grid-cols-4" style={{ border: `1px solid ${C.borderSoft}`, borderRadius: 2, marginBottom: 14 }}>
        <Stat C={C} label="VISITS" value={p.visits.toLocaleString('en-US')} />
        <Stat C={C} label="SESSIONS" value={p.sessions.toLocaleString('en-US')} />
        <Stat C={C} label="FIRST SEEN" value={<Tooltip content={formatIsoDate(p.first_seen, tz)}>{shortDate(p.first_seen, tz)}</Tooltip>} />
        <Stat C={C} label="LAST SEEN" value={<RelativeTime iso={p.last_seen} />} last />
      </div>

      <dl style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 16, rowGap: 6, margin: '0 0 18px' }}>
        <Fact C={C} label="LOCATION">{place ?? '—'}</Fact>
        <Fact C={C} label="DEVICE">
          {device !== null ? `${device}${p.device.device !== null ? ` · ${p.device.device}` : ''}` : '—'}
        </Fact>
        <Fact C={C} label={detail.ip.addresses.length > 1 ? 'IP ADDRESSES' : 'IP ADDRESS'}>
          {detail.ip.status === 'shown' ? (
            <span data-testid="person-ip" style={{ fontFamily: monoFont, fontSize: 11 }}>
              {detail.ip.addresses.map((a, i) => (
                <span key={a.address} style={{ display: 'block' }}>
                  {a.address}
                  {i === 0 && detail.ip.addresses.length > 1 ? <span style={{ color: C.muted }}> · latest</span> : null}
                </span>
              ))}
            </span>
          ) : (
            <span data-testid="person-ip-status" style={{ color: C.muted }}>{IP_STATUS_COPY[detail.ip.status]}</span>
          )}
        </Fact>
      </dl>

      <Kicker C={C}>{`TIMELINE · ${detail.total_sessions.toLocaleString('en-US')} SESSION${detail.total_sessions === 1 ? '' : 'S'}`}</Kicker>
      <div style={{ marginTop: 8 }}>
        {days.map((d) => (
          <section key={d.key} data-testid="person-day" style={{ marginBottom: 14 }}>
            <div style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 1.5, color: C.muted, margin: '0 0 6px' }}>{d.label}</div>
            {d.sessions.map((s) => (
              <SessionCard
                key={s.session_key}
                C={C}
                session={s}
                tz={tz}
                ip={s.ip_index !== null ? detail.ip.addresses[s.ip_index]?.address ?? null : null}
                onOpenRecord={onOpenRecord}
              />
            ))}
          </section>
        ))}
      </div>

      {page < detail.total_pages ? (
        <button
          type="button"
          onClick={() => setPage((n) => n + 1)}
          disabled={loadingMore}
          style={buttonStyle(C, loadingMore)}
        >
          {loadingMore ? 'LOADING…' : 'LOAD OLDER SESSIONS'}
        </button>
      ) : null}

      {can_forget && p.key_kind !== 'overflow' ? (
        <div style={{ marginTop: 18, paddingTop: 12, borderTop: `1px solid ${C.borderSoft}` }}>
          <button type="button" data-testid="person-forget" onClick={() => setConfirming(true)} style={{ ...buttonStyle(C, false), color: C.coralFg, borderColor: C.coralFg }}>
            FORGET THIS VISITOR
          </button>
          {forgetFailed ? <Muted C={C}>The visitor could not be forgotten. Try again.</Muted> : null}
          <ConfirmDialog
            open={confirming}
            onClose={() => setConfirming(false)}
            title="Forget this visitor?"
            body="Sill deletes this visitor's page history and any stored IP address that no other visitor uses. The signed visit records stay; they never contained this visitor's identity. If the same browser visits again, Sill starts a new history for it."
            confirmLabel="Forget visitor"
            confirmTone="coral"
            onConfirm={forget}
          />
        </div>
      ) : null}
    </div>
  );
}

function shortDate(iso: string, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone, month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(iso));
  } catch {
    return iso.slice(0, 10);
  }
}

function buttonStyle(C: Tokens, busy: boolean): CSSProperties {
  return {
    marginTop: 4,
    background: 'transparent',
    border: `1px solid ${C.border}`,
    borderRadius: 2,
    padding: '5px 10px',
    fontFamily: monoFont,
    fontSize: 9,
    letterSpacing: 1.5,
    color: C.muted,
    cursor: busy ? 'not-allowed' : 'pointer',
  };
}

function Kicker({ C, children, testid }: { C: Tokens; children: ReactNode; testid?: string }): JSX.Element {
  return (
    <div data-testid={testid} style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 1.5, color: C.muted }}>
      {children}
    </div>
  );
}

function Muted({ C, children, testid }: { C: Tokens; children: ReactNode; testid?: string }): JSX.Element {
  return (
    <div data-testid={testid} style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, marginTop: 6 }}>
      {children}
    </div>
  );
}

function Stat({ C, label, value, last = false }: { C: Tokens; label: string; value: ReactNode; last?: boolean }): JSX.Element {
  return (
    <div style={{ padding: '8px 10px', borderRight: last ? 'none' : `1px solid ${C.borderSoft}` }}>
      <div style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 1.5, color: C.muted }}>{label}</div>
      <div style={{ fontFamily: monoFont, fontSize: 13, color: C.text, marginTop: 2 }}>{value}</div>
    </div>
  );
}

function Fact({ C, label, children }: { C: Tokens; label: string; children: ReactNode }): JSX.Element {
  return (
    <>
      <dt style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 1.5, color: C.muted, paddingTop: 2 }}>{label}</dt>
      <dd style={{ margin: 0, fontSize: 13, color: C.body }}>{children}</dd>
    </>
  );
}

function SessionCard({
  C,
  session: s,
  tz,
  ip,
  onOpenRecord,
}: {
  C: Tokens;
  session: PersonSession;
  tz: string;
  ip: string | null;
  onOpenRecord?: ((record_id: string) => void) | undefined;
}): JSX.Element {
  const span = s.page_count > 1 ? `${clock(s.started_at, tz)}–${clock(s.ended_at, tz)}` : clock(s.started_at, tz);
  const where = fullPlaceLabel(s.location);
  return (
    <div data-testid="person-session" style={{ border: `1px solid ${C.borderSoft}`, borderRadius: 2, marginBottom: 8 }}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1" style={{ padding: '7px 10px', borderBottom: `1px solid ${C.borderSoft}`, background: C.surface2 }}>
        <span style={{ fontFamily: monoFont, fontSize: 11, color: C.text }}>{span}</span>
        <span style={{ fontFamily: monoFont, fontSize: 10, color: C.muted }}>
          {`${formatDuration(s.duration_seconds)} · ${s.page_count} page${s.page_count === 1 ? '' : 's'}`}
        </span>
        {where !== null || ip !== null ? (
          <span style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, marginLeft: 'auto' }}>
            {[where, ip].filter((v): v is string => v !== null).join(' · ')}
          </span>
        ) : null}
      </div>
      <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {s.pages.map((pg, i) => (
          <li key={`${pg.hit_at}-${i}`} className="flex items-center gap-3" style={{ padding: '6px 10px', borderBottom: i === s.pages.length - 1 ? 'none' : `1px solid ${C.borderSoft}` }}>
            <span style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, minWidth: 40 }}>{clock(pg.hit_at, tz)}</span>
            <span style={{ fontFamily: monoFont, fontSize: 11, color: C.text, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {pg.page_path ?? 'page'}
              {pg.search_term !== null ? <span style={{ color: C.skyFg }}>{`  “${pg.search_term}”`}</span> : null}
            </span>
            {pg.page_kind !== null && pg.page_kind !== 'other' ? (
              <span style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 1.5, color: C.muted }}>{pg.page_kind.toUpperCase()}</span>
            ) : null}
            {onOpenRecord !== undefined ? (
              <button
                type="button"
                onClick={() => onOpenRecord(pg.record_id)}
                aria-label="Open the signed visit record"
                style={{ background: 'transparent', border: 'none', fontFamily: monoFont, fontSize: 9, letterSpacing: 1.5, color: C.muted, cursor: 'pointer', padding: 0 }}
              >
                RECORD
              </button>
            ) : null}
          </li>
        ))}
        {s.pages_truncated ? (
          <li style={{ padding: '6px 10px', fontFamily: monoFont, fontSize: 10, color: C.muted }}>
            {`${(s.page_count - s.pages.length).toLocaleString('en-US')} more pages in this session`}
          </li>
        ) : null}
      </ol>
    </div>
  );
}
